/**
 * Runtime Application Self-Protection (RASP) Constants
 * 
 * Constants used by the RASP security system
 */

// RASP Service Status
export const RASP_SERVICE_STATUS = {
    UNINITIALIZED: 'uninitialized',
    INITIALIZING: 'initializing',
    ACTIVE: 'active',
    ERROR: 'error',
    DISABLED: 'disabled'
};

// Threat Levels
export const THREAT_LEVELS = {
    NONE: 'none',
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
};

// Security Event Types
export const SECURITY_EVENT_TYPES = {
    // Runtime tampering
    RUNTIME_TAMPERING: 'runtime_tampering',
    MEMORY_MODIFICATION: 'memory_modification',
    CODE_INJECTION: 'code_injection',
    
    // API abuse
    API_ABUSE: 'api_abuse',
    RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
    UNAUTHORIZED_ACCESS: 'unauthorized_access',
    SUSPICIOUS_PAYLOAD: 'suspicious_payload',
    
    // Network security
    NETWORK_ANOMALY: 'network_anomaly',
    INSECURE_CONNECTION: 'insecure_connection',
    CERTIFICATE_MISMATCH: 'certificate_mismatch',
    
    // Device environment
    DEVICE_ANOMALY: 'device_anomaly',
    SUSPICIOUS_ENVIRONMENT: 'suspicious_environment',
    EMULATOR_DETECTED: 'emulator_detected',
    
    // App state integrity
    STATE_CORRUPTION: 'state_corruption',
    UNEXPECTED_BEHAVIOR: 'unexpected_behavior',
    CONFIGURATION_TAMPERING: 'configuration_tampering'
};

// Detection Thresholds (CONSERVATIVE - reduced to avoid false positives)
export const DETECTION_THRESHOLDS = {
    // API call rate limits (calls per minute) - INCREASED
    API_RATE_LIMIT: 300, // Increased from 60 to 300
    API_BURST_LIMIT: 50, // Increased from 10 to 50 calls per 10 seconds

    // Memory check intervals (milliseconds) - LESS FREQUENT
    MEMORY_CHECK_INTERVAL: 300000, // Increased from 30s to 5 minutes

    // Network monitoring intervals - LESS FREQUENT
    NETWORK_CHECK_INTERVAL: 300000, // Increased from 1 minute to 5 minutes

    // App state integrity checks - LESS FREQUENT
    STATE_CHECK_INTERVAL: 60000, // Increased from 15s to 1 minute

    // Maximum security events before escalation - MORE TOLERANT
    MAX_EVENTS_PER_MINUTE: 20, // Increased from 5 to 20
    MAX_EVENTS_PER_HOUR: 100 // Increased from 20 to 100
};

// Response Actions
export const RESPONSE_ACTIONS = {
    LOG_ONLY: 'log_only',
    NOTIFY_USER: 'notify_user',
    REPORT_BACKEND: 'report_backend',
    DEGRADE_FUNCTIONALITY: 'degrade_functionality',
    FORCE_RECONNECT: 'force_reconnect',
    EMERGENCY_SHUTDOWN: 'emergency_shutdown'
};

// Threat Level Response Mapping (Frontend-only mode - no backend reporting)
export const THREAT_RESPONSE_MAP = {
    [THREAT_LEVELS.NONE]: [RESPONSE_ACTIONS.LOG_ONLY],
    [THREAT_LEVELS.LOW]: [RESPONSE_ACTIONS.LOG_ONLY],
    [THREAT_LEVELS.MEDIUM]: [RESPONSE_ACTIONS.LOG_ONLY, RESPONSE_ACTIONS.NOTIFY_USER],
    [THREAT_LEVELS.HIGH]: [RESPONSE_ACTIONS.LOG_ONLY, RESPONSE_ACTIONS.NOTIFY_USER, RESPONSE_ACTIONS.DEGRADE_FUNCTIONALITY],
    [THREAT_LEVELS.CRITICAL]: [RESPONSE_ACTIONS.LOG_ONLY, RESPONSE_ACTIONS.NOTIFY_USER, RESPONSE_ACTIONS.FORCE_RECONNECT]
};

// Event Severity Mapping
export const EVENT_SEVERITY_MAP = {
    [SECURITY_EVENT_TYPES.RUNTIME_TAMPERING]: THREAT_LEVELS.CRITICAL,
    [SECURITY_EVENT_TYPES.MEMORY_MODIFICATION]: THREAT_LEVELS.HIGH,
    [SECURITY_EVENT_TYPES.CODE_INJECTION]: THREAT_LEVELS.CRITICAL,
    
    [SECURITY_EVENT_TYPES.API_ABUSE]: THREAT_LEVELS.MEDIUM,
    [SECURITY_EVENT_TYPES.RATE_LIMIT_EXCEEDED]: THREAT_LEVELS.LOW,
    [SECURITY_EVENT_TYPES.UNAUTHORIZED_ACCESS]: THREAT_LEVELS.HIGH,
    [SECURITY_EVENT_TYPES.SUSPICIOUS_PAYLOAD]: THREAT_LEVELS.MEDIUM,
    
    [SECURITY_EVENT_TYPES.NETWORK_ANOMALY]: THREAT_LEVELS.MEDIUM,
    [SECURITY_EVENT_TYPES.INSECURE_CONNECTION]: THREAT_LEVELS.HIGH,
    [SECURITY_EVENT_TYPES.CERTIFICATE_MISMATCH]: THREAT_LEVELS.HIGH,
    
    [SECURITY_EVENT_TYPES.DEVICE_ANOMALY]: THREAT_LEVELS.LOW,
    [SECURITY_EVENT_TYPES.SUSPICIOUS_ENVIRONMENT]: THREAT_LEVELS.MEDIUM,
    [SECURITY_EVENT_TYPES.EMULATOR_DETECTED]: THREAT_LEVELS.LOW,
    
    [SECURITY_EVENT_TYPES.STATE_CORRUPTION]: THREAT_LEVELS.MEDIUM, // Reduced from HIGH to avoid startup false positives
    [SECURITY_EVENT_TYPES.UNEXPECTED_BEHAVIOR]: THREAT_LEVELS.LOW, // Reduced from MEDIUM
    [SECURITY_EVENT_TYPES.CONFIGURATION_TAMPERING]: THREAT_LEVELS.HIGH
};

// Detection Configuration
export const DETECTION_CONFIG = {
    // Enable/disable individual detectors
    RUNTIME_TAMPERING_ENABLED: true,
    API_ABUSE_ENABLED: true,
    MEMORY_INTEGRITY_ENABLED: true,
    NETWORK_SECURITY_ENABLED: true,
    DEVICE_ENVIRONMENT_ENABLED: true,
    APP_STATE_INTEGRITY_ENABLED: true,
    
    // Performance settings
    MAX_CONCURRENT_CHECKS: 3,
    CHECK_TIMEOUT: 5000, // 5 seconds
    
    // Memory management
    MAX_EVENT_HISTORY: 100,
    CLEANUP_INTERVAL: 300000 // 5 minutes
};

// Security Messages
export const SECURITY_MESSAGES = {
    THREAT_DETECTED: 'Security threat detected',
    SYSTEM_COMPROMISED: 'System integrity compromised',
    SUSPICIOUS_ACTIVITY: 'Suspicious activity detected',
    SECURITY_CHECK_FAILED: 'Security check failed',
    PROTECTION_ACTIVE: 'Security protection active'
};

// Logging Configuration
export const LOG_LEVELS = {
    NONE: 0,     // No logging
    ERROR: 1,    // Only errors
    WARN: 2,     // Warnings and errors
    INFO: 3,     // Important info, warnings, errors
    DEBUG: 4,    // All logs including debug
    VERBOSE: 5   // Everything including initialization details
};

// Current log level (change this to control verbosity)
export const CURRENT_LOG_LEVEL = __DEV__ ? LOG_LEVELS.WARN : LOG_LEVELS.ERROR;

// Development mode exceptions (things to ignore in dev)
export const DEV_MODE_EXCEPTIONS = {
    IGNORE_EMULATOR_DETECTION: true,        // Don't alert on emulators in dev
    IGNORE_DEBUGGING_TOOLS: true,           // Don't alert on dev tools in dev
    IGNORE_CONSOLE_MODIFICATIONS: true,     // Don't alert on console mods in dev
    IGNORE_ENVIRONMENT_MODIFICATIONS: true, // Don't alert on setTimeout mods in dev
    REDUCE_INITIALIZATION_LOGS: true        // Reduce init logging in dev
};

// API Endpoints for security reporting
export const SECURITY_API_ENDPOINTS = {
    REPORT_SECURITY_EVENT: '/api/security/events',
    GET_SECURITY_POLICY: '/api/security/policy',
    UPDATE_THREAT_LEVEL: '/api/security/threat-level'
};
